import { type Dispatch, type SetStateAction, useState } from 'react';

import { Product } from '@eneco/flows/types';
import { useFormatter } from '@eneco/i18n';
import { Fields as SummaryStepFields } from '@sitecore/types/SummaryStep';

import {
  checkOfferIncludesDynamicPricingProduct,
  constructTextualDataKey,
  getCashbackTexts,
} from '../../../../shared/helpers';
import { checkOnDiscountType } from '../../../../shared/helpers/sitecore';
import { useProductItemContent } from '../../../../shared/hooks';
import { EnergyType, Offer } from '../../../../shared/types';

interface UseSummaryOfferCardOptions {
  offer: Offer;
  energyType?: EnergyType;
  textualData?: SummaryStepFields;
}

/**
 * View-model for the exp-n-522 summary sidebar offer card.
 * Uses SummaryStep Sitecore fields only (no OfferStep giftData / TOU dialog).
 */
export const useSummaryOfferCard = ({
  offer,
  energyType = EnergyType.Electricity,
  textualData,
}: UseSummaryOfferCardOptions) => {
  const { format, currency } = useFormatter();
  const [costPeriod, setCostPeriod] = useState('costsMonthly');

  const textualDataKey = checkOnDiscountType(
    constructTextualDataKey(offer.contractSpan, energyType),
    offer?.discountType,
  );

  const { getProductDetails } = useProductItemContent(textualData?.productData?.productDataList);
  const productDetails = getProductDetails(textualDataKey);

  const cashbackTexts = getCashbackTexts(
    offer?.costDiscounts?.cashback?.type,
    textualData?.promotionTexts,
    undefined,
    offer?.costDiscounts?.cashback?.value,
  );

  const getCostPerPeriod = (data: Offer | Product) =>
    costPeriod === 'costsMonthly' ? data.costsMonthly : data.costsYearly;

  const isDynamicOffer = checkOfferIncludesDynamicPricingProduct(offer);

  const getPeriodLabel = () => {
    if (costPeriod === 'costsMonthly') {
      return isDynamicOffer
        ? textualData?.productData?.perMonthEstimatedLabel?.value
        : textualData?.productData?.perMonthLabel?.value;
    }
    return isDynamicOffer ? 'verwacht per jaar' : 'geschat per jaar';
  };

  const cashbackRibbonTitle = cashbackTexts
    ? format(cashbackTexts.title, {
        [`${offer?.costDiscounts?.cashback?.type}`]: currency.euroNoFractionDigits(
          offer?.costDiscounts?.cashback?.value ?? 0,
        ),
      })
    : undefined;

  return {
    productDetails,
    cashbackTexts,
    cashbackRibbonTitle,
    textualDataKey,
    costPeriod,
    setCostPeriod: setCostPeriod as Dispatch<SetStateAction<string>>,
    price: {
      value: getCostPerPeriod(offer)?.vatIncluded ?? 0,
      period: getPeriodLabel(),
    },
  };
};
